/* eslint-disable max-len */
/* eslint-disable sonarjs/cognitive-complexity */
/* eslint-disable sonarjs/no-duplicate-string */

import {
  takeEvery, put, select, call, takeLatest,
} from 'redux-saga/effects';

import {
  MODALS, API_URLS, FETCH_OPTIONS,
} from '../constants';
import {
  getLastEventId, getLastDate, getEvents, getTennisEvents, getTennisLastDate, getTennisLastEventId,
} from '../selectors/events';
import {
  adjustAccordingToTimezone,
  generateQueryString,
  getEventTimeHeaders,
} from '../utils/common';
import { requestGet, requestPost } from '../utils/request';
import { getTimezone } from '../selectors/common';
import { aBookEvents, aBookTennisEvents, aGetEvents, aGetTennisEvents, aResetEventDetails, aResetTennisEventDetails, aUnbookEvents, aUnbookTennisEvents } from '../reducers';
import { aBookUnbookEventsSuccess, aBookUnbookTennisEventsSuccess, aGetEventsSuccess, aGetTennisEventsSuccess } from '../reducers/events';
import { aSetIsFetching, aSetOpenModal } from '../reducers/common';

/**
 * @namespace sagas/events
 *
 */

/**
 * @memberof sagas/events
 * @typedef User
 * @type {object}
 * @property {Array} permissions
 * @property {boolean} requireNewPassword
 * @property {Array} roles
 * @property {number} userId
 * @property {string} username
 */
/**
 * @memberof sagas/events
 * @typedef getEvents/response.data
 * @type {object}
 * @property {User} user
 * @property {string} sessionId
 */

/**
 * @memberof sagas/events
 * @async
 * @param {object} action
 */
function* getEventsSaga(action) {
  let response;
  try {
    const lastEventId = yield select(getLastEventId);
    const lastDate = yield select(getLastDate);
    const filters = {
      ...action.payload,
    };
    if (lastEventId) {
      filters.lastEventId = lastEventId;
    }
    if (lastDate) {
      filters.lastDate = lastDate;
    }

    const timezone = yield select(getTimezone);

    // Adjust Date and Time filters for api call according to timezone selected
    if (filters?.filterStartDate && filters?.filterEndDate) {
      const updatedFilterStartDate = adjustAccordingToTimezone(timezone, filters.filterStartDate);
      const updatedFilterEndDate = adjustAccordingToTimezone(timezone, filters.filterEndDate);

      filters.filterStartDate = updatedFilterStartDate;
      filters.filterEndDate = updatedFilterEndDate;
    }

    const params = generateQueryString(filters);

    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.GET_EVENTS,
      value: true,
    }));

    response = yield call(requestGet, API_URLS.GET_EVENTS + params, true);

    if (response.status === -1) {
      throw Error(response.error.message);
    } else {
      const events = yield select(getEvents);
      const updatedEvents = [...events, ...response.data.events];

      const timeHeaders = getEventTimeHeaders(updatedEvents);

      yield put(aGetEventsSuccess({
        events: updatedEvents,
        timeHeaders,
        lastEventId: response.data.lastEventId,
        lastDate: response.data.lastDate,
      }));
    }
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.GET_EVENTS,
      value: false,
    }));
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.modal ? error.message : MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.GET_EVENTS,
      value: false,
    }));
  }
}

/**
 * @param {object} action
 */
function* getTennisEventsSaga(action) {
  let response;
  try {
    const lastEventId = yield select(getTennisLastEventId);
    const lastDate = yield select(getTennisLastDate);
    const filters = {
      ...action.payload,
    };

    if (lastEventId) {
      filters.lastEventId = lastEventId;
    }
    if (lastDate) {
      filters.lastDate = lastDate;
    }

    const timezone = yield select(getTimezone);

    // Adjust Date and Time filters for api call according to timezone selected
    if (filters?.filterStartDate && filters?.filterEndDate) {
      const updatedFilterStartDate = adjustAccordingToTimezone(timezone, filters.filterStartDate);
      const updatedFilterEndDate = adjustAccordingToTimezone(timezone, filters.filterEndDate);

      filters.filterStartDate = updatedFilterStartDate;
      filters.filterEndDate = updatedFilterEndDate;
    }

    const params = generateQueryString(filters);

    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.GET_TENNIS_EVENTS,
      value: true,
    }));

    response = yield call(requestGet, API_URLS.GET_TENNIS_EVENTS + params, true);

    if (response.status === -1) {
      throw Error(response.error.message);
    } else {
      const events = yield select(getTennisEvents);
      const updatedEvents = [...events, ...response.data.events];

      const timeHeaders = getEventTimeHeaders(updatedEvents);

      yield put(aGetTennisEventsSuccess({
        tennisEvents: updatedEvents,
        tennisTimeHeaders: timeHeaders,
        tennisLastEventId: response.data.lastEventId,
        tennisLastDate: response.data.lastDate,
      }));
    }
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.GET_TENNIS_EVENTS,
      value: false,
    }));
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.modal ? error.message : MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.GET_TENNIS_EVENTS,
      value: false,
    }));
  }
}
/**
 * @memberof sagas/events
 * @typedef bookEvents/response.data
 */
/**
 * @memberof sagas/events
 * @async
 * @param {object} action
 */
function* bookEvents(action) {
  let response;
  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.BOOK,
      value: true,
    }));
    response = yield call(requestPost, API_URLS.BOOK_EVENTS, action.payload, true);

    if (response.status === -1) {
      throw Error(response.error.message);
    } else {
      const currentEvents = yield select(getEvents);
      const bookedEvents = response.data.events;
      bookedEvents.forEach((event) => {
        const bookEventIndex = currentEvents.findIndex((m) => m.matchId === event.matchId);
        currentEvents[bookEventIndex] = event;
      });
      yield put(aBookUnbookEventsSuccess({
        events: currentEvents,
      }));
    }
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.BOOK,
      value: false,
    }));
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.modal ? error.message : MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.BOOK,
      value: false,
    }));
  }
}

/**
 * @param {object} action
 */
function* bookTennisEvents(action) {
  let response;
  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.BOOK_TENNIS,
      value: true,
    }));
    response = yield call(requestPost, API_URLS.BOOK_TENNIS_EVENTS, action.payload, true);

    if (response.status === -1) {
      throw Error(response.error.message);
    } else {
      const currentEvents = yield select(getTennisEvents);
      const bookedEvents = response.data.events;
      bookedEvents.forEach((event) => {
        const bookEventIndex = currentEvents.findIndex((m) => m.matchId === event.matchId);
        currentEvents[bookEventIndex] = event;
      });
      yield put(aBookUnbookTennisEventsSuccess({
        events: currentEvents,
      }));
    }
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.BOOK_TENNIS,
      value: false,
    }));
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.modal ? error.message : MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.BOOK_TENNIS,
      value: false,
    }));
  }
}

/**
 * @param {object} action
 */
function* unbookEvents(action) {
  let response;
  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.UNBOOK,
      value: true,
    }));
    response = yield call(requestPost, API_URLS.UNBOOK_EVENTS, action.payload, true);

    if (response.status === -1) {
      throw Error(response.error.message);
    } else {
      const currentEvents = yield select(getEvents);
      const unBookEvents = response.data.events;
      unBookEvents.forEach((event) => {
        const unBookedEventIndex = currentEvents.findIndex((m) => m.matchId === event.matchId);
        currentEvents[unBookedEventIndex] = event;
      });
      yield put(aBookUnbookEventsSuccess({
        events: currentEvents,
      }));
    }
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.UNBOOK,
      value: false,
    }));
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.modal ? error.message : MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.UNBOOK,
      value: false,
    }));
  }
}

/**
 * @param {object} action
 */
function* unbookTennisEvents(action) {
  let response;
  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.UNBOOK_TENNIS,
      value: true,
    }));
    response = yield call(requestPost, API_URLS.UNBOOK_TENNIS_EVENTS, action.payload, true);

    if (response.status === -1) {
      throw Error(response.error.message);
    } else {
      const currentEvents = yield select(getTennisEvents);
      const unBookEvents = response.data.events;
      unBookEvents.forEach((event) => {
        const unBookedEventIndex = currentEvents.findIndex((m) => m.matchId === event.matchId);
        currentEvents[unBookedEventIndex] = event;
      });
      yield put(aBookUnbookTennisEventsSuccess({
        events: currentEvents,
      }));
    }
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.UNBOOK_TENNIS,
      value: false,
    }));
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.modal ? error.message : MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.UNBOOK_TENNIS,
      value: false,
    }));
  }
}

/**
 *
 */
function* resetDetailsForEvents() {
  yield put(aGetEventsSuccess({
    events: [],
    lastEventId: null,
    lastDate: null,
    timeHeaders: [],
  }));
}

/**
 *
 */
function* resetDetailsForTennisEvents() {
  yield put(aGetTennisEventsSuccess({
    tennisEvents: [],
    tennisLastEventId: null,
    tennisLastDate: null,
    tennisTimeHeaders: [],
  }));
}

/**
 * @memberof sagas/events
 * @async
 * @returns {void}
 */
export default function* eventsSaga() {
  yield takeEvery(aGetEvents, getEventsSaga);
  yield takeEvery(aGetTennisEvents, getTennisEventsSaga);
  yield takeLatest(aBookEvents, bookEvents);
  yield takeLatest(aUnbookEvents, unbookEvents);
  yield takeLatest(aBookTennisEvents, bookTennisEvents);
  yield takeLatest(aUnbookTennisEvents, unbookTennisEvents);
  yield takeLatest(aResetEventDetails, resetDetailsForEvents);
  yield takeLatest(aResetTennisEventDetails, resetDetailsForTennisEvents);
}

